import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { SvgAttachment } from '@actual-app/components/icons/v1';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import type {
  AttachmentLocalState,
  TransactionAttachmentEntity,
  TransactionAttachmentView,
} from '@actual-app/core/types/models';
import { useQuery } from '@tanstack/react-query';

import {
  ATTACHMENT_FILE_ACCEPT,
  formatAttachmentBytes,
  readAttachmentFile,
  saveAttachmentFile,
} from '#attachments/files';
import {
  useAddAttachmentMutation,
  useDeleteAttachmentMutation,
  useReplaceAttachmentMutation,
  useRetryAttachmentMutation,
} from '#attachments/mutations';
import { attachmentQueries } from '#attachments/queries';
import {
  Modal,
  ModalCloseButton,
  ModalHeader,
  ModalTitle,
} from '#components/common/Modal';
import { useSyncServerStatus } from '#hooks/useSyncServerStatus';

import { AttachmentPreviewModal } from './AttachmentPreviewModal';

export type TransactionAttachmentsModalProps = {
  transactionId: string;
};

type SyncServerStatus = 'offline' | 'no-server' | 'online';

function AttachmentStatus({
  localState,
  syncServerStatus,
}: {
  localState: AttachmentLocalState;
  syncServerStatus: SyncServerStatus;
}) {
  switch (localState) {
    case 'uploading':
      return <Trans>Uploading</Trans>;
    case 'downloading':
      return <Trans>Downloading</Trans>;
    case 'missing':
      return <Trans>Missing locally</Trans>;
    case 'failed':
      return <Trans>Unavailable on server</Trans>;
    case 'local-only':
      return <Trans>Local only</Trans>;
    case 'cached':
      return syncServerStatus === 'online' ? (
        <Trans>Available on server</Trans>
      ) : (
        <Trans>Local only</Trans>
      );
    case 'downloadable':
      return syncServerStatus === 'no-server' ? (
        <Trans>Local only</Trans>
      ) : (
        <Trans>Available on server</Trans>
      );
    default:
      return <Trans>Local only</Trans>;
  }
}

function AttachmentSyncSummary({
  attachments,
  syncServerStatus,
}: {
  attachments: TransactionAttachmentView[];
  syncServerStatus: SyncServerStatus;
}) {
  if (syncServerStatus === 'no-server') {
    return (
      <Trans>
        Attachments are stored locally. Connect a sync server to sync them
        across devices.
      </Trans>
    );
  }
  if (syncServerStatus === 'offline') {
    return (
      <Trans>
        Attachments are stored locally and will sync when the server reconnects.
      </Trans>
    );
  }
  if (
    attachments.some(
      attachment =>
        attachment.local_state === 'missing' ||
        attachment.local_state === 'failed',
    )
  ) {
    return <Trans>Some attachments need attention.</Trans>;
  }
  if (
    attachments.some(
      attachment =>
        attachment.local_state === 'uploading' ||
        attachment.local_state === 'downloading',
    )
  ) {
    return <Trans>Attachments are syncing with your server.</Trans>;
  }
  return <Trans>Attachments are synced with your server.</Trans>;
}

function canReadAttachment(localState: AttachmentLocalState): boolean {
  return !['downloading', 'failed', 'missing'].includes(localState);
}

function canRetryAttachment(localState: AttachmentLocalState): boolean {
  return localState === 'failed' || localState === 'missing';
}

export function TransactionAttachmentsModal({
  transactionId,
}: TransactionAttachmentsModalProps) {
  const { t } = useTranslation();
  const syncServerStatus = useSyncServerStatus();
  const addInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const [replacementTarget, setReplacementTarget] =
    useState<TransactionAttachmentEntity | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<TransactionAttachmentEntity | null>(null);
  const [preview, setPreview] = useState<TransactionAttachmentEntity | null>(
    null,
  );
  const [operationError, setOperationError] = useState<string | null>(null);
  const {
    data: attachments = [],
    isLoading,
    error: listError,
  } = useQuery(attachmentQueries.list(transactionId));
  const addAttachment = useAddAttachmentMutation();
  const replaceAttachment = useReplaceAttachmentMutation();
  const deleteAttachment = useDeleteAttachmentMutation();
  const retryAttachment = useRetryAttachmentMutation();

  const readAttachment = async (attachment: TransactionAttachmentEntity) => {
    return send('transaction-attachment-read', { id: attachment.id });
  };

  const showPreview = (attachment: TransactionAttachmentEntity) => {
    setOperationError(null);
    setPreview(attachment);
  };

  const saveAttachment = async (attachment: TransactionAttachmentEntity) => {
    try {
      setOperationError(null);
      const result = await readAttachment(attachment);
      await saveAttachmentFile({
        contents: result.contents,
        filename: result.attachment.original_filename,
        mimeType: result.attachment.mime_type,
      });
    } catch (error) {
      setOperationError(
        error instanceof Error
          ? error.message
          : t('The attachment could not be saved.'),
      );
    }
  };

  const handleAdd = async (file: File) => {
    try {
      setOperationError(null);
      await addAttachment.mutateAsync({
        transactionId,
        file: await readAttachmentFile(file),
      });
    } catch (error) {
      setOperationError(
        error instanceof Error
          ? error.message
          : t('The attachment could not be added.'),
      );
    }
  };

  const handleReplace = async (
    attachment: TransactionAttachmentEntity,
    file: File,
  ) => {
    try {
      setOperationError(null);
      if (preview?.id === attachment.id) {
        setPreview(null);
      }
      await replaceAttachment.mutateAsync({
        attachmentId: attachment.id,
        transactionId,
        file: await readAttachmentFile(file),
      });
    } catch (error) {
      setOperationError(
        error instanceof Error
          ? error.message
          : t('The attachment could not be replaced.'),
      );
    }
  };

  const handleDelete = async (attachment: TransactionAttachmentEntity) => {
    try {
      setOperationError(null);
      await deleteAttachment.mutateAsync({
        attachmentId: attachment.id,
        transactionId,
      });
      if (preview?.id === attachment.id) {
        setPreview(null);
      }
      setDeleteTarget(null);
    } catch (error) {
      setOperationError(
        error instanceof Error
          ? error.message
          : t('The attachment could not be moved to Trash.'),
      );
    }
  };

  const handleRetry = async (attachment: TransactionAttachmentView) => {
    try {
      setOperationError(null);
      await retryAttachment.mutateAsync({
        attachmentId: attachment.id,
        transactionId,
      });
    } catch (error) {
      setOperationError(
        error instanceof Error
          ? error.message
          : t('The attachment could not be retried.'),
      );
    }
  };

  const isMutating =
    addAttachment.isPending ||
    replaceAttachment.isPending ||
    deleteAttachment.isPending ||
    retryAttachment.isPending;

  return (
    <Modal
      name="transaction-attachments"
      containerProps={{ style: { width: 720, maxWidth: '94vw' } }}
    >
      {({ state }) => (
        <>
          <ModalHeader
            title={
              <ModalTitle
                title={t('Attachments ({{count}})', {
                  count: attachments.length,
                })}
              />
            }
            rightContent={
              <ModalCloseButton
                onPress={() => {
                  setPreview(null);
                  state.close();
                }}
              />
            }
          />

          <Text
            role="status"
            style={{
              color: theme.pageTextSubdued,
              marginBottom: 14,
              textAlign: 'center',
            }}
          >
            <AttachmentSyncSummary
              attachments={attachments}
              syncServerStatus={syncServerStatus}
            />
          </Text>

          <input
            ref={addInputRef}
            type="file"
            accept={ATTACHMENT_FILE_ACCEPT}
            aria-label={t('Select attachment')}
            style={{ display: 'none' }}
            onChange={event => {
              const file = event.currentTarget.files?.[0];
              event.currentTarget.value = '';
              if (file) {
                void handleAdd(file);
              }
            }}
          />
          <input
            ref={replaceInputRef}
            type="file"
            accept={ATTACHMENT_FILE_ACCEPT}
            aria-label={t('Select replacement attachment')}
            style={{ display: 'none' }}
            onChange={event => {
              const file = event.currentTarget.files?.[0];
              const target = replacementTarget;
              event.currentTarget.value = '';
              setReplacementTarget(null);
              if (file && target) {
                void handleReplace(target, file);
              }
            }}
          />

          <View style={{ alignItems: 'flex-start', marginBottom: 12 }}>
            <Button
              variant="primary"
              isDisabled={isMutating}
              onPress={() => addInputRef.current?.click()}
            >
              <SvgAttachment width={15} height={15} />
              <Trans>Add attachment</Trans>
            </Button>
          </View>

          {(operationError || listError) && (
            <Text
              role="alert"
              style={{ color: theme.errorText, marginBottom: 10 }}
            >
              {operationError ||
                (listError instanceof Error
                  ? listError.message
                  : t('Attachments could not be loaded.'))}
            </Text>
          )}

          {isLoading ? (
            <Text>
              <Trans>Loading attachments...</Trans>
            </Text>
          ) : attachments.length === 0 ? (
            <View
              style={{
                padding: 24,
                alignItems: 'center',
                border: `1px dashed ${theme.tableBorder}`,
                borderRadius: 6,
              }}
            >
              <Text style={{ color: theme.pageTextSubdued }}>
                <Trans>No attachments yet.</Trans>
              </Text>
            </View>
          ) : (
            <View style={{ gap: 8 }}>
              {attachments.map(attachment => (
                <View
                  key={attachment.id}
                  data-testid={`attachment-${attachment.id}`}
                  style={{
                    border: `1px solid ${theme.tableBorder}`,
                    borderRadius: 6,
                    padding: 10,
                    gap: 8,
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <SvgAttachment width={18} height={18} />
                    <View style={{ minWidth: 0, flex: 1 }}>
                      <Text
                        style={{
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {attachment.original_filename}
                      </Text>
                      <Text
                        style={{
                          color: theme.pageTextSubdued,
                          fontSize: 12,
                        }}
                      >
                        {attachment.mime_type === 'application/pdf'
                          ? t('PDF')
                          : t('Image')}{' '}
                        · {formatAttachmentBytes(attachment.byte_size)} ·{' '}
                        {new Intl.DateTimeFormat().format(
                          new Date(attachment.created_at),
                        )}
                      </Text>
                      <Text
                        data-testid={`attachment-status-${attachment.id}`}
                        style={{
                          color:
                            attachment.local_state === 'failed' ||
                            attachment.local_state === 'missing'
                              ? theme.errorText
                              : theme.pageTextSubdued,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        <AttachmentStatus
                          localState={attachment.local_state}
                          syncServerStatus={syncServerStatus}
                        />
                      </Text>
                    </View>
                  </View>

                  {deleteTarget?.id === attachment.id ? (
                    <View
                      style={{
                        gap: 8,
                      }}
                    >
                      <Text>
                        {t('Move {{filename}} to Trash?', {
                          filename: attachment.original_filename,
                        })}
                      </Text>
                      <Text
                        style={{
                          color: theme.pageTextSubdued,
                          fontSize: 12,
                        }}
                      >
                        <Trans>
                          The attachment remains recoverable for 30 days before
                          permanent cleanup.
                        </Trans>
                      </Text>
                      <View
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'flex-end',
                          gap: 8,
                        }}
                      >
                        <Button
                          isDisabled={isMutating}
                          onPress={() => setDeleteTarget(null)}
                        >
                          <Trans>Cancel</Trans>
                        </Button>
                        <Button
                          variant="primary"
                          isDisabled={isMutating}
                          aria-label={t('Confirm move {{filename}} to Trash', {
                            filename: attachment.original_filename,
                          })}
                          onPress={() => void handleDelete(attachment)}
                        >
                          <Trans>Move to Trash</Trans>
                        </Button>
                      </View>
                    </View>
                  ) : (
                    <View
                      style={{
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        gap: 8,
                      }}
                    >
                      <Button
                        aria-label={t('Preview {{filename}}', {
                          filename: attachment.original_filename,
                        })}
                        isDisabled={
                          isMutating ||
                          !canReadAttachment(attachment.local_state)
                        }
                        onPress={() => showPreview(attachment)}
                      >
                        <Trans>Preview</Trans>
                      </Button>
                      {canRetryAttachment(attachment.local_state) && (
                        <Button
                          aria-label={t('Retry {{filename}}', {
                            filename: attachment.original_filename,
                          })}
                          isDisabled={isMutating}
                          onPress={() => void handleRetry(attachment)}
                        >
                          <Trans>Retry</Trans>
                        </Button>
                      )}
                      <Button
                        aria-label={t('Replace {{filename}}', {
                          filename: attachment.original_filename,
                        })}
                        isDisabled={isMutating}
                        onPress={() => {
                          setReplacementTarget(attachment);
                          replaceInputRef.current?.click();
                        }}
                      >
                        <Trans>Replace</Trans>
                      </Button>
                      <Button
                        aria-label={t('Save {{filename}} as', {
                          filename: attachment.original_filename,
                        })}
                        isDisabled={
                          isMutating ||
                          !canReadAttachment(attachment.local_state)
                        }
                        onPress={() => void saveAttachment(attachment)}
                      >
                        <Trans>Save as</Trans>
                      </Button>
                      <Button
                        aria-label={t('Move {{filename}} to Trash', {
                          filename: attachment.original_filename,
                        })}
                        isDisabled={isMutating}
                        onPress={() => setDeleteTarget(attachment)}
                      >
                        <Trans>Move to Trash</Trans>
                      </Button>
                    </View>
                  )}
                </View>
              ))}
            </View>
          )}

          {preview && (
            <AttachmentPreviewModal
              attachment={preview}
              onClose={() => setPreview(null)}
            />
          )}
        </>
      )}
    </Modal>
  );
}
